'use client';

import {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { Box } from '@terra/ui-styled-system';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { MergeView } from '@codemirror/merge';
import {
  search,
  searchKeymap,
  highlightSelectionMatches,
  openSearchPanel,
} from '@codemirror/search';
import {
  mergeThemeLeft,
  mergeThemeRight,
  searchPanelTheme,
} from './merge-theme';
import { chunkHighlightExtension, setChunkHighlight } from './chunk-highlight';
import { comparerDiffConfig } from './diff-config';

export type CodeMirrorMergeViewHandle = {
  goToNext: () => void;
  goToPrev: () => void;
  getChunkCount: () => number;
  openSearch: () => void;
};

type CodeMirrorMergeViewProps = {
  leftContent: string;
  rightContent: string;
  collapseUnchanged?: boolean;
};

export const CodeMirrorMergeView = forwardRef<
  CodeMirrorMergeViewHandle,
  CodeMirrorMergeViewProps
>(function CodeMirrorMergeView(
  { leftContent, rightContent, collapseUnchanged },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mergeViewRef = useRef<MergeView | null>(null);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(-1);

  useEffect(() => {
    if (!containerRef.current) return;

    const baseExtensions = [
      lineNumbers(),
      EditorState.readOnly.of(true),
      search({ top: true }),
      keymap.of(searchKeymap),
      highlightSelectionMatches(),
      searchPanelTheme,
      chunkHighlightExtension,
    ];

    const view = new MergeView({
      a: {
        doc: leftContent,
        extensions: [mergeThemeLeft, ...baseExtensions],
      },
      b: {
        doc: rightContent,
        extensions: [mergeThemeRight, ...baseExtensions],
      },
      parent: containerRef.current,
      highlightChanges: true,
      gutter: true,
      diffConfig: comparerDiffConfig,
      collapseUnchanged: collapseUnchanged
        ? { margin: 3, minSize: 4 }
        : undefined,
    });

    mergeViewRef.current = view;
    setCurrentChunkIndex(-1);

    return () => {
      view.destroy();
      mergeViewRef.current = null;
    };
  }, [leftContent, rightContent, collapseUnchanged]);

  const scrollToChunk = useCallback((index: number) => {
    const view = mergeViewRef.current;

    if (!view || view.chunks.length === 0) return;

    const clampedIndex = Math.max(0, Math.min(index, view.chunks.length - 1));

    setCurrentChunkIndex(clampedIndex);

    const chunk = view.chunks[clampedIndex];

    // Highlight the current chunk in both editors and scroll to it
    view.a.dispatch({
      effects: [
        setChunkHighlight.of({ from: chunk.fromA, to: chunk.endA }),
        EditorView.scrollIntoView(chunk.fromA, { y: 'center' }),
      ],
    });
    view.b.dispatch({
      effects: [
        setChunkHighlight.of({ from: chunk.fromB, to: chunk.endB }),
        EditorView.scrollIntoView(chunk.fromB, { y: 'center' }),
      ],
    });
  }, []);

  const goToNext = useCallback(() => {
    const view = mergeViewRef.current;

    if (!view || view.chunks.length === 0) return;
    const nextIndex =
      currentChunkIndex + 1 >= view.chunks.length ? 0 : currentChunkIndex + 1;

    scrollToChunk(nextIndex);
  }, [currentChunkIndex, scrollToChunk]);

  const goToPrev = useCallback(() => {
    const view = mergeViewRef.current;

    if (!view || view.chunks.length === 0) return;
    const prevIndex =
      currentChunkIndex - 1 < 0
        ? view.chunks.length - 1
        : currentChunkIndex - 1;

    scrollToChunk(prevIndex);
  }, [currentChunkIndex, scrollToChunk]);

  const getChunkCount = useCallback(() => {
    const view = mergeViewRef.current;

    if (!view) return 0;

    return view.chunks.length;
  }, []);

  const openSearch = useCallback(() => {
    const view = mergeViewRef.current;

    if (!view) return;
    // Open search in the left (a) editor; user can click the right one to search there
    view.a.focus();
    openSearchPanel(view.a);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      goToNext,
      goToPrev,
      getChunkCount,
      openSearch,
    }),
    [goToNext, goToPrev, getChunkCount, openSearch],
  );

  return (
    <Box
      ref={containerRef}
      h="full"
      w="full"
      overflow="hidden"
      className="cm-merge-wrapper"
      css={{ '& .cm-mergeView': { height: '100%' } }}
    />
  );
});
